import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_PREFIX_GRANT = 'w86_ml_grant_';
const STORAGE_PREFIX_EMAIL = 'w86_ml_email_';

/**
 * Shared hook for magic-link verification flow.
 *
 * Handles:
 * - Reading `verifyToken` from URL query params
 * - Calling the verify endpoint
 * - Storing the grant token + email in sessionStorage
 * - Scrubbing the verifyToken from the URL
 *
 * @param {string} resourceId - The form token or componentId used as storage key
 * @param {function} verifyFn - async (verificationToken) => { email, verificationGrantToken }
 * @returns {{ grantToken: string, verifiedEmail: string, verificationError: string }}
 */
export default function useMagicLinkVerification(resourceId, verifyFn) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [grantToken, setGrantToken] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_PREFIX_GRANT + resourceId) || ''; }
    catch { return ''; }
  });
  const [verifiedEmail, setVerifiedEmail] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_PREFIX_EMAIL + resourceId) || ''; }
    catch { return ''; }
  });
  const [verificationError, setVerificationError] = useState('');

  useEffect(() => {
    const verifyToken = searchParams.get('verifyToken');
    if (!verifyToken || !resourceId) return;

    const next = new URLSearchParams(searchParams);
    next.delete('verifyToken');
    setSearchParams(next, { replace: true });

    (async () => {
      try {
        const data = await verifyFn(verifyToken);
        if (data?.email && data?.verificationGrantToken) {
          setGrantToken(data.verificationGrantToken);
          setVerifiedEmail(data.email);
          try {
            sessionStorage.setItem(STORAGE_PREFIX_GRANT + resourceId, data.verificationGrantToken);
            sessionStorage.setItem(STORAGE_PREFIX_EMAIL + resourceId, data.email);
          } catch { /* sessionStorage unavailable */ }
        }
      } catch {
        setVerificationError('Email verification failed. The link may have expired.');
      }
    })();
  }, [searchParams, setSearchParams, resourceId, verifyFn]);

  const clearGrantToken = () => {
    setGrantToken('');
    setVerifiedEmail('');
    try {
      sessionStorage.removeItem(STORAGE_PREFIX_GRANT + resourceId);
      sessionStorage.removeItem(STORAGE_PREFIX_EMAIL + resourceId);
    } catch { /* sessionStorage unavailable */ }
  };

  return { grantToken, verifiedEmail, verificationError, clearGrantToken };
}
